import { Component, computed, EventEmitter, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FatipOidcAuthService } from '../../core/auth/fatip-oidc.service';
import { VouchersService, PilotVoucher, VoucherCreateRequest } from '../../core/api/vouchers.service';
import { renderHttpError } from '../../core/api/error-renderer';
import { FapIconComponent } from '../../core/ui/fap-icon.component';
import { DialogComponent } from '../../core/ui/dialog.component';
import { FAP_DATE_FORMAT } from '../../core/ui/date-format';

/**
 * 0.62.x ADR-088 — Voucher-Create-Modal.
 *
 * Wird vom Header-Button "FATIP Voucher erstellen" geöffnet. Form mit
 * Vorname + Nachname + optional Email. Nach Generieren: Code wird
 * angezeigt mit Copy-Button. User kann den Code via Zwischenablage
 * weitergeben (Slack/Email/handschriftlich).
 *
 * U12 (issue #386): Die Shell (Backdrop, role="dialog"-Modal) lebt im
 * gemeinsamen Wrapper <fap-dialog> (core/ui/dialog.component.ts). Der
 * Wrapper emittiert closed bei Escape und Backdrop-Klick; (closed) muendet
 * in closed wie vorher der Backdrop- und der ×-Click. Das Modal ist immer
 * offen, solange es gemountet ist (die Shell regelt das @if).
 */
@Component({
  selector: 'fap-voucher-create-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, FapIconComponent, DialogComponent],
  template: `
    <form #voucherForm="ngForm" (ngSubmit)="submit()" novalidate>
    <fap-dialog
      [open]="true"
      titleKey="i18n.fap.voucher.create.title"
      (closed)="closed.emit()">
      @if (created(); as v) {
        <!-- Erfolg-View: Code anzeigen + Copy-Button -->
        <div class="modal-body voucher-success">
          <p class="muted">{{ 'i18n.fap.voucher.create.success_intro' | translate }}</p>
          <div class="voucher-code-display">
            <code class="voucher-code">{{ v.code }}</code>
            <button type="button" class="fap-btn fap-btn--small" (click)="copyCode(v.code)">
              <fap-icon name="copy" [size]="14"></fap-icon>
              {{ copied() ? ('i18n.fap.voucher.create.copied' | translate) : ('i18n.fap.voucher.create.copy' | translate) }}
            </button>
          </div>
          <dl class="voucher-summary">
            <dt>{{ 'i18n.fap.voucher.create.invitee' | translate }}</dt>
            <dd>{{ v.invitee_first }} {{ v.invitee_last }}</dd>
            <dt>{{ 'i18n.fap.voucher.create.expires_at' | translate }}</dt>
            <dd>{{ v.expires_at | date: fapDateFormat }}</dd>
          </dl>
          <p class="muted small">
            {{ 'i18n.fap.voucher.create.redeem_url_hint' | translate }}
            <code>{{ redeemUrl() }}</code>
          </p>
        </div>
      } @else {
        <!-- Form-View: Eingabe Vorname/Nachname/Email.
             U12 (Review): Das <form> umschliesst den ganzen <fap-dialog>,
             damit der Submit-Button (type="submit") im [dialog-actions]-Slot
             innerhalb der Form liegt und (ngSubmit) bei Enter im Feld UND
             beim Button-Click feuert; der direkte (click)-Handler von
             0.61.23.3 ist damit entfallen. -->
        <div class="modal-body">
          <p class="muted">{{ 'i18n.fap.voucher.create.intro' | translate }}</p>

          <label class="fap-field">
            <span>{{ 'i18n.fap.voucher.create.first_name' | translate }} *</span>
            <input type="text" name="first" [(ngModel)]="form.invitee_first" required maxlength="100"
                   [placeholder]="'i18n.fap.voucher.create.first_name_placeholder' | translate" />
          </label>

          <label class="fap-field">
            <span>{{ 'i18n.fap.voucher.create.last_name' | translate }} *</span>
            <input type="text" name="last" [(ngModel)]="form.invitee_last" required maxlength="100"
                   [placeholder]="'i18n.fap.voucher.create.last_name_placeholder' | translate" />
          </label>

          <label class="fap-field">
            <span>{{ 'i18n.fap.voucher.create.email' | translate }}</span>
            <input type="email" name="email" [(ngModel)]="form.invitee_email" maxlength="200"
                   [placeholder]="'i18n.fap.voucher.create.email_placeholder' | translate" />
            <small class="muted">{{ 'i18n.fap.voucher.create.email_hint' | translate }}</small>
          </label>

          <label class="fap-field">
            <span>{{ 'i18n.fap.voucher.create.language' | translate }}</span>
            <select name="language" [(ngModel)]="form.language">
              <option value="de">Deutsch</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="it">Italiano</option>
            </select>
          </label>

          @if (errorMsg()) {
            <div class="fap-alert fap-alert--warn">{{ errorMsg() }}</div>
          }
        </div>
      }
      <div dialog-actions>
        @if (created(); as v) {
          <button type="button" class="fap-btn fap-btn--primary" (click)="closed.emit()">
            {{ 'i18n.fap.voucher.create.done' | translate }}
          </button>
        } @else {
          <button type="button" class="fap-btn" (click)="closed.emit()">
            {{ 'i18n.fap.voucher.create.cancel' | translate }}
          </button>
          <button type="submit" class="fap-btn fap-btn--primary"
                  [disabled]="submitting() || !canSubmit()">
            @if (submitting()) {
              {{ 'i18n.fap.voucher.create.generating' | translate }}
            } @else {
              {{ 'i18n.fap.voucher.create.generate' | translate }}
            }
          </button>
        }
      </div>
    </fap-dialog>
    </form>
  `,
  styles: [`
    /* U12: die Overlay-/Modal-Chrome lebt im gemeinsamen Wrapper
       <fap-dialog>; hier bleiben die Styles des projizierten Inhalts. */
    .modal-body { padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
    .fap-field { display: flex; flex-direction: column; gap: 4px; }
    .fap-field span { font-size: 0.92rem; }
    .fap-field input { padding: 8px 10px; border-radius: 4px; font-size: 0.95rem; }
    .fap-field small { font-size: 0.8rem; }
    .voucher-success { gap: 18px; }
    .voucher-code-display {
      display: flex; align-items: center; gap: 10px;
      padding: 14px 16px; background: var(--ob-surface-pale); border: 2px solid var(--ob-accent-green);
      border-radius: 6px;
    }
    .voucher-code { flex: 1; font-size: 1.4rem; font-weight: 700; color: var(--ob-accent-navy); font-family: ui-monospace, monospace; letter-spacing: 0.05em; }
    .voucher-summary { display: grid; grid-template-columns: 140px 1fr; gap: 6px 12px; margin: 4px 0; font-size: 0.92rem; }
    .voucher-summary dt { color: var(--ob-text-faint); }
    .voucher-summary dd { margin: 0; font-weight: 500; }
  `],
})
export class VoucherCreateModalComponent {
  @Output() closed = new EventEmitter<void>();
  private svc = inject(VouchersService);
  private translate = inject(TranslateService);
  private oidc = inject(FatipOidcAuthService);

  protected readonly fapDateFormat = FAP_DATE_FORMAT;

  /**
   * The address the invited person redeems the code at. The configured public
   * address the backend serves on `GET /v1/oidc-config` (`portal_url`, from
   * `NOTIF_PORTAL_URL`) is the authority when one is served; the origin the
   * operator is reading this modal at is the fallback when it is not. A literal
   * is right on one deployment and wrong on every other one.
   *
   * This is the hand-delivery path only. The emailed link is built by the
   * backend from `NOTIF_PORTAL_URL` (`services/org/internal/notif/hook.go`) and
   * carries the code as a query parameter; this one is the bare page.
   */
  readonly redeemUrl = computed(() => {
    const served = this.oidc.oidcConfig()?.portal_url?.trim();
    return (served || window.location.origin) + '/voucher';
  });

  form: VoucherCreateRequest = { invitee_first: '', invitee_last: '', invitee_email: '', language: 'de' };
  created = signal<PilotVoucher | null>(null);
  submitting = signal(false);
  errorMsg = signal<string | null>(null);
  copied = signal(false);

  canSubmit(): boolean {
    // 0.61.23.3 — defensiv gegen undefined wenn ngModel nicht bindet.
    return (this.form.invitee_first || '').trim().length > 0
        && (this.form.invitee_last  || '').trim().length > 0;
  }

  submit() {
    if (!this.canSubmit() || this.submitting()) return;
    this.submitting.set(true);
    this.errorMsg.set(null);
    this.svc.create({
      invitee_first: this.form.invitee_first.trim(),
      invitee_last: this.form.invitee_last.trim(),
      invitee_email: this.form.invitee_email?.trim() || undefined,
      language: this.form.language || 'de',
    }).subscribe({
      next: v => {
        this.created.set(v);
        this.submitting.set(false);
      },
      error: err => {
        this.errorMsg.set(renderHttpError(err, this.translate));
        this.submitting.set(false);
      },
    });
  }

  copyCode(code: string) {
    navigator.clipboard?.writeText(code).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2500);
    });
  }
}
